/**
 * Pure converters between the structured editor's shape and the API's
 * workflow shape. Extracted from HandoffRulePage so they can be unit
 * tested without pulling in the page's router/context dependencies.
 */
import {
  type ActionGroup as EditorActionGroup,
  type ActionRow as EditorActionRow,
  type ActionRowType,
  type ActionSubtype,
  type PushField,
  type PushOption,
  type PushTarget,
  type TransferSettings,
} from '@/components/actions/ActionEditor'
import {
  type ActionContact,
  type RoleWithMembers,
  type Staff,
} from '@/lib/api/directory.client'
import { parseTargets } from '@/lib/handoff-mentions'
import {
  ActionSubtype as ApiActionSubtype,
  INTEGRATIONS,
  extractPhoneAndExtension,
  getHandoffFields,
  getIntegrationActionSubtypes,
  getPushIntegrationConfig,
  parseTransferActionParameters,
  resolvePushSubtype,
  type ActionGroup as ApiActionGroup,
  type Action as ApiAction,
  type Integration,
  type RecipientEntry,
  type ResolvedConversationDescriptionOptions,
} from '@super/shared-types'

type HandoffFieldConfigShape = ReturnType<typeof getHandoffFields>[number]
type RowRecipient = {
  value: string
  sourceRoleMemberId: string | null
  staffId?: string
}

const newId = () => crypto.randomUUID()

// action_contacts has no TRANSFER channel: a TRANSFER row's links are PHONE_CALL links.
export const contactChannelOf = (
  rowType: Exclude<ActionRowType, 'PUSH_INTEGRATION'>,
): 'PHONE_CALL' | 'SMS' | 'EMAIL' =>
  rowType === 'TRANSFER' ? 'PHONE_CALL' : rowType

/**
 * Coerce a raw value (string from an input, number from metadata, etc.)
 * to the field's expected type. HandoffFieldConfig.type defaults to
 * 'numeric' when omitted, so we preserve that contract here too.
 *
 * Used for both metadata pre-population and live user input.
 */
export function coerceFieldValue(
  raw: unknown,
  type: 'numeric' | 'text' = 'numeric',
): unknown {
  if (raw === undefined || raw === null || raw === '') return undefined
  if (type === 'numeric') {
    const n = Number(raw)
    return Number.isNaN(n) ? undefined : n
  }
  return raw
}

/**
 * Include-toggle defaults for a push target that has a registry `description`
 * config (its description is composed centrally with include-toggles), or
 * undefined for targets that don't. The rule editor uses this to decide whether
 * to render the "Include" dropdown — one source so the dropdown can't drift
 * from where composition actually happens.
 */
export function conversationDescriptionDefaultsFor(
  integrationType: string,
  actionSubtype: string | null,
): ResolvedConversationDescriptionOptions | undefined {
  // resolvePushSubtype mirrors dispatch's null fallback so legacy rules with
  // actionSubtype: null show the dropdown the same way they're routed.
  const config = getPushIntegrationConfig(
    integrationType,
    resolvePushSubtype(integrationType, actionSubtype),
  )
  return config?.description?.defaultSections
}

/** True when the option describes the same (integration, subtype, variant)
 *  triple as the target. The matching tuple drives picker selection,
 *  parameter pre-population, and dropdown-source fetch eligibility. */
export function pushOptionMatchesTarget(
  option: PushOption,
  target: Pick<PushTarget, 'integrationId' | 'actionSubtype' | 'parameters'>,
): boolean {
  return (
    option.integrationId === target.integrationId &&
    option.actionSubtype === target.actionSubtype &&
    option.defaultParameters?.taskVariant === target.parameters?.taskVariant
  )
}

export function findPushOption(
  options: PushOption[],
  target: Pick<PushTarget, 'integrationId' | 'actionSubtype' | 'parameters'>,
): PushOption | undefined {
  return options.find(option => pushOptionMatchesTarget(option, target))
}

function toEditorField(field: HandoffFieldConfigShape): PushField {
  const inputType = field.inputType === 'dropdown' ? 'dropdown' : 'text'
  return {
    key: field.key,
    label: field.label,
    inputType,
    dropdownSource: field.dropdownSource,
    description: field.description,
    placeholder: field.placeholder,
    valueType: field.type,
    directoryAssignee: field.directoryAssignee,
    dependsOnField: field.dependsOnField,
  }
}

export function buildDefaultParameters(
  fields: HandoffFieldConfigShape[],
  metadata: Record<string, unknown>,
  variantId: string | undefined,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {}
  if (variantId) defaults.taskVariant = variantId
  for (const f of fields) {
    if (!f.metadataKey) continue
    const value = coerceFieldValue(metadata[f.metadataKey], f.type)
    if (value !== undefined) defaults[f.key] = value
  }
  return defaults
}

/**
 * Build the editor's push picker options. Mirrors the modal's
 * createPushIntegrationDropdownItems: fan out per
 * (integration × subtype × variant), preserving variant ids and
 * metadata-derived defaults so they round-trip through PushTarget.
 */
export function buildPushOptions(integrations: Integration[]): PushOption[] {
  const items: PushOption[] = []
  for (const integration of integrations) {
    const integrationType = integration.type as keyof typeof INTEGRATIONS
    const integrationConfig = INTEGRATIONS[integrationType] as
      | { NAME?: string; LOGO_PATH?: string }
      | undefined
    const friendly = integrationConfig?.NAME ?? integration.type
    const iconUrl = integrationConfig?.LOGO_PATH
    const actionSubtypes = getIntegrationActionSubtypes(integrationType)
    const metadata = (integration.metadata || {}) as Record<string, unknown>

    if (!actionSubtypes || actionSubtypes.length === 0) {
      // Mirrors the modal's bare-integration entry: no subtypes → single
      // option with actionSubtype: null.
      items.push({
        id: integration.id,
        integrationId: integration.id,
        integrationType: integration.type,
        integrationName: friendly,
        actionSubtype: null,
        label: friendly,
        iconUrl,
      })
      continue
    }

    for (const actionSubtype of actionSubtypes) {
      if (actionSubtype.variants && actionSubtype.variants.length > 0) {
        for (const variant of actionSubtype.variants) {
          const fields = getHandoffFields(
            integrationType,
            actionSubtype.type as ApiActionSubtype,
            variant.id,
          )
          items.push({
            id: `${integration.id}:${actionSubtype.type}:${variant.id}`,
            integrationId: integration.id,
            integrationType: integration.type,
            integrationName: friendly,
            actionSubtype: actionSubtype.type as ActionSubtype,
            label: `${friendly} - ${variant.name}`,
            iconUrl,
            fields: fields.map(toEditorField),
            defaultParameters: buildDefaultParameters(
              fields,
              metadata,
              variant.id,
            ),
          })
        }
      } else {
        const fields = getHandoffFields(
          integrationType,
          actionSubtype.type as ApiActionSubtype,
        )
        items.push({
          id: `${integration.id}:${actionSubtype.type}`,
          integrationId: integration.id,
          integrationType: integration.type,
          integrationName: friendly,
          actionSubtype: actionSubtype.type as ActionSubtype,
          label: `${friendly} - ${actionSubtype.name}`,
          iconUrl,
          fields: fields.map(toEditorField),
          defaultParameters: buildDefaultParameters(
            fields,
            metadata,
            undefined,
          ),
        })
      }
    }
  }
  return items
}

// Optional directory context for the load path. When supplied, literal
// phones/emails that came from a linked role or staff are collapsed back
// into `@Name` chips so re-saving preserves the mention.
export interface WorkflowToEditorDirectoryContext {
  contactsByGroupId: Map<string, ActionContact[]>
  roles: RoleWithMembers[]
  staff: Staff[]
}

/**
 * Hydrate editor groups from saved API groups. Pre-populates push-target
 * parameters with metadata-derived defaults from the matching push option,
 * mirroring the modal's edit-load behaviour. Saved params take
 * precedence; defaults only fill gaps.
 */
export function workflowToEditorGroups(
  apiGroups: ApiActionGroup[],
  pushOptions: PushOption[],
  directory?: WorkflowToEditorDirectoryContext,
): EditorActionGroup[] {
  return apiGroups.map(apiGroup => {
    const actionsByType = new Map<ActionRowType, EditorActionRow>()
    const apiTypeOf = (action: ApiAction) =>
      action.actionDefinition?.actionType ?? action.actionType
    const transferParameters = parseTransferActionParameters({
      actionType: 'TRANSFER',
      parameters: apiGroup.actions?.find(
        action => apiTypeOf(action) === 'TRANSFER',
      )?.parameters,
    })
    // The Transfer call row takes the place of the first PHONE_CALL row, so
    // the rows keep their order.
    const transferSlot =
      apiGroup.actions?.find(action => apiTypeOf(action) === 'PHONE_CALL') ??
      apiGroup.actions?.find(action => apiTypeOf(action) === 'TRANSFER')
    for (const action of apiGroup.actions ?? []) {
      const apiType = apiTypeOf(action)
      if (!apiType) continue
      // A converted group's PHONE_CALL rows are a copy of its TRANSFER entries.
      if (
        transferParameters &&
        (apiType === 'TRANSFER' || apiType === 'PHONE_CALL')
      ) {
        if (action !== transferSlot) continue
        actionsByType.set('TRANSFER', {
          id: newId(),
          type: 'TRANSFER',
          target: transferParameters.recipients
            .map(entry =>
              entry.extension
                ? `${entry.number}#${entry.extension}`
                : entry.number,
            )
            .join(', '),
        })
        continue
      }
      if (apiType === 'TRANSFER') continue
      const editorType = apiType as ActionRowType
      const params = (action.parameters ?? {}) as Record<string, unknown>
      const existing = actionsByType.get(editorType)

      if (editorType === 'PUSH_INTEGRATION') {
        const integrationId =
          (action as { integrationId?: string | null }).integrationId ?? ''
        const actionSubtype = ((action as { actionSubtype?: string | null })
          .actionSubtype ?? null) as ActionSubtype | null
        if (!integrationId) continue

        const matchedOption = findPushOption(pushOptions, {
          integrationId,
          actionSubtype,
          parameters: params,
        })
        const mergedParams: Record<string, unknown> = { ...params }
        if (matchedOption?.defaultParameters) {
          const defaults = matchedOption.defaultParameters
          for (const [key, value] of Object.entries(defaults)) {
            if (mergedParams[key] !== undefined) continue
            // A cascading field's default belongs with its parent's default
            // (e.g. the default stage is a stage of the default pipeline);
            // never pair it with a saved parent override from another group.
            const parentKey = matchedOption.fields?.find(
              f => f.key === key,
            )?.dependsOnField
            if (
              parentKey &&
              mergedParams[parentKey] !== undefined &&
              String(mergedParams[parentKey]) !==
                String(defaults[parentKey] ?? '')
            )
              continue
            mergedParams[key] = value
          }
        }
        const pushTarget = {
          integrationId,
          actionSubtype,
          parameters: mergedParams,
        }
        if (existing) {
          existing.pushTargets = [...(existing.pushTargets ?? []), pushTarget]
        } else {
          actionsByType.set(editorType, {
            id: newId(),
            type: editorType,
            target: '',
            pushTargets: [pushTarget],
          })
        }
        continue
      }

      // Phone / SMS / Email — collapse multiple same-type actions into a
      // single comma-separated row so the editor stays compact.
      const literal =
        editorType === 'EMAIL'
          ? ((params.email as string | undefined) ?? '')
          : ((params.number as string | undefined) ?? '')
      if (!literal) continue
      if (existing) {
        existing.target = existing.target
          ? `${existing.target}, ${literal}`
          : literal
      } else {
        actionsByType.set(editorType, {
          id: newId(),
          type: editorType,
          target: literal,
        })
      }
    }
    // An empty role has no materialized action, so no row was built above.
    // Synthesize a row of the link's channel so its @Role chip can survive.
    if (directory && apiGroup.id) {
      for (const contact of directory.contactsByGroupId.get(apiGroup.id) ??
        []) {
        if (!contact.roleId || !contact.actionType) continue
        const rowType: ActionRowType =
          transferParameters && contact.actionType === 'PHONE_CALL'
            ? 'TRANSFER'
            : contact.actionType
        if (actionsByType.has(rowType)) continue
        const role = directory.roles.find(({ id }) => id === contact.roleId)
        if (!role || role.members.length > 0) continue
        actionsByType.set(rowType, { id: newId(), type: rowType, target: '' })
      }
    }

    const editorGroup: EditorActionGroup = {
      id: apiGroup.id ?? newId(),
      name: apiGroup.name ?? '',
      when: apiGroup.selectionCriteria ?? '',
      actions: Array.from(actionsByType.values()),
    }
    if (transferParameters) {
      const { recipients: _recipients, ...transfer } = transferParameters
      editorGroup.transfer = transfer
    }

    if (directory) {
      injectMentions(editorGroup, apiGroup.id, directory)
    }

    return editorGroup
  })
}

// Reverse of editorRowToApiActions: relabel materialized phones/emails back into
// `@Name` tokens so reloading preserves the authored mention.
function injectMentions(
  group: EditorActionGroup,
  apiGroupId: string | undefined,
  directory: WorkflowToEditorDirectoryContext,
): void {
  if (!apiGroupId) return
  const contacts = directory.contactsByGroupId.get(apiGroupId) ?? []
  if (contacts.length === 0) return

  for (const row of group.actions) {
    if (row.type === 'PUSH_INTEGRATION') continue
    const channel = contactChannelOf(row.type)

    const addressOf = (staffMember: Staff): string | null =>
      row.type === 'EMAIL' ? staffMember.email : staffMember.phone
    const literalTokens = row.target
      .split(',')
      .map(piece => piece.trim())
      .filter(Boolean)
    // Counted multiset: each mention claims one occurrence, duplicates survive.
    const remainingCounts = new Map<string, number>()
    for (const token of literalTokens) {
      remainingCounts.set(token, (remainingCounts.get(token) ?? 0) + 1)
    }
    const hasRemaining = (address: string) =>
      (remainingCounts.get(address) ?? 0) > 0
    const claimOne = (address: string) => {
      const count = remainingCounts.get(address) ?? 0
      if (count > 0) remainingCounts.set(address, count - 1)
    }
    const applicableContacts = contacts.filter(
      contact => contact.actionType === null || contact.actionType === channel,
    )

    const roleMentions: string[] = []
    const staffMentions: string[] = []
    const mentionedRoleIds = new Set<string>()
    const mentionedStaffIds = new Set<string>()
    // Addresses each chip stands for, for the phone-order pass below. Keyed by
    // display label, so it assumes role/staff names are unique.
    const chipAddresses = new Map<string, string[]>()

    // Explicit staff claim their address first, ahead of any role they're in.
    for (const contact of applicableContacts) {
      if (!contact.staffId || mentionedStaffIds.has(contact.staffId)) continue
      const staffMember = directory.staff.find(
        ({ id }) => id === contact.staffId,
      )
      const address = staffMember && addressOf(staffMember)
      if (!staffMember || !address || !hasRemaining(address)) continue
      const label = `@${staffMember.name}`
      staffMentions.push(label)
      mentionedStaffIds.add(staffMember.id)
      chipAddresses.set(label, [address])
      claimOne(address)
    }

    // A role earns its chip when any member's address still has an occurrence left.
    for (const contact of applicableContacts) {
      if (!contact.roleId || mentionedRoleIds.has(contact.roleId)) continue
      const role = directory.roles.find(({ id }) => id === contact.roleId)
      if (!role) continue
      const memberAddresses = role.members
        .map(member => addressOf(member.staff))
        .filter((address): address is string => !!address)
      if (!memberAddresses.some(hasRemaining)) continue
      const label = `@${role.name}`
      roleMentions.push(label)
      mentionedRoleIds.add(role.id)
      const claimed: string[] = []
      for (const address of memberAddresses) {
        if (hasRemaining(address)) {
          claimed.push(address)
          claimOne(address)
        }
      }
      chipAddresses.set(label, claimed)
    }

    // Empty roles have no member to collapse; keep their chip from the link alone.
    for (const contact of applicableContacts) {
      if (!contact.roleId || mentionedRoleIds.has(contact.roleId)) continue
      const role = directory.roles.find(({ id }) => id === contact.roleId)
      if (!role || role.members.length > 0) continue
      roleMentions.push(`@${role.name}`)
      mentionedRoleIds.add(role.id)
    }

    // Unclaimed literals survive in authored order.
    const survivingLiterals: string[] = []
    for (const token of literalTokens) {
      if (hasRemaining(token)) {
        survivingLiterals.push(token)
        claimOne(token)
      }
    }

    // Phone is dialed in sequence, so reload it in authored order; SMS/email fan
    // out in parallel and keep the kind-grouped order (byte-identical to before).
    row.target =
      channel === 'PHONE_CALL'
        ? orderPhoneChipsAsAuthored(
            literalTokens,
            roleMentions,
            staffMentions,
            survivingLiterals,
            chipAddresses,
          ).join(', ')
        : [...roleMentions, ...staffMentions, ...survivingLiterals].join(', ')
  }
}

// Reorder a phone row's chips into authored order, recovered from the stored
// tokens: a role claims its member-run, a direct mention its own token — so a
// mention before vs after its role keeps the right slot. Ties keep resolved order.
function orderPhoneChipsAsAuthored(
  tokens: string[],
  roleMentions: string[],
  staffMentions: string[],
  survivingLiterals: string[],
  chipAddresses: Map<string, string[]>,
): string[] {
  // Sort key for a chip that never got a slot — sorts last.
  const UNANCHORED = Infinity
  const consumed = new Array<boolean>(tokens.length).fill(false)
  const anchorIndexByChip = new Map<string, number>()
  const staffLabelByAddress = new Map(
    staffMentions.map(label => [chipAddresses.get(label)?.[0], label]),
  )

  // Length of the role's block at `start`. `ordered` matches members in position
  // order — distinguishes a direct mention next to its role from the role's own
  // block. `!ordered` is a multiset fallback for a drifted stored block.
  const roleRunLength = (
    start: number,
    addresses: string[],
    ordered: boolean,
  ): number => {
    if (ordered) {
      if (start + addresses.length > tokens.length) return 0
      for (
        let memberOffset = 0;
        memberOffset < addresses.length;
        memberOffset++
      ) {
        const at = start + memberOffset
        if (consumed[at] || tokens[at] !== addresses[memberOffset]) return 0
      }
      return addresses.length
    }
    const remaining = new Map<string, number>()
    for (const address of addresses) {
      remaining.set(address, (remaining.get(address) ?? 0) + 1)
    }
    let length = 0
    for (
      let tokenIndex = start;
      tokenIndex < tokens.length && !consumed[tokenIndex];
      tokenIndex++
    ) {
      const token = tokens[tokenIndex]
      if ((remaining.get(token) ?? 0) === 0) break
      remaining.set(token, remaining.get(token)! - 1)
      length++
    }
    return length
  }

  // First unanchored role whose block starts at `start` (with its length, since
  // a drifted `!ordered` run can be shorter than the full member list), or null.
  const roleRunAt = (start: number, ordered: boolean) => {
    for (const label of roleMentions) {
      if (anchorIndexByChip.has(label)) continue
      const length = roleRunLength(
        start,
        chipAddresses.get(label) ?? [],
        ordered,
      )
      if (length > 0) return { label, length }
    }
    return null
  }
  const claimRoleRun = (label: string, start: number, length: number) => {
    anchorIndexByChip.set(label, start)
    for (let offset = 0; offset < length; offset++)
      consumed[start + offset] = true
  }

  // Pass 1 — position-order matching + direct staff mentions, left to right.
  for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex++) {
    if (consumed[tokenIndex]) continue
    const run = roleRunAt(tokenIndex, true)
    if (run) {
      claimRoleRun(run.label, tokenIndex, run.length)
      continue
    }
    const staffLabel = staffLabelByAddress.get(tokens[tokenIndex])
    if (staffLabel && !anchorIndexByChip.has(staffLabel)) {
      anchorIndexByChip.set(staffLabel, tokenIndex)
      consumed[tokenIndex] = true
    }
    // else: a surviving literal — anchored by its own index in the pass below.
  }

  // Pass 2 — rescue any role whose stored block drifted out of position order.
  for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex++) {
    if (consumed[tokenIndex]) continue
    const run = roleRunAt(tokenIndex, false)
    if (run) claimRoleRun(run.label, tokenIndex, run.length)
  }

  // Pass 3 — the surviving tokens, each at its own slot. A dial order may list
  // a person or role more than once, so a survivor that starts a mentioned
  // role's block or is a mentioned staff member's number reloads as that chip.
  const survivorCounts = new Map<string, number>()
  for (const literal of survivingLiterals) {
    survivorCounts.set(literal, (survivorCounts.get(literal) ?? 0) + 1)
  }
  const isSurvivor = (tokenIndex: number) =>
    !consumed[tokenIndex] && (survivorCounts.get(tokens[tokenIndex]) ?? 0) > 0
  const claimSurvivor = (tokenIndex: number) => {
    consumed[tokenIndex] = true
    const token = tokens[tokenIndex]
    survivorCounts.set(token, (survivorCounts.get(token) ?? 0) - 1)
  }
  const repeatedRoleRunFits = (start: number, addresses: string[]) => {
    if (addresses.length === 0 || start + addresses.length > tokens.length)
      return false
    const needed = new Map<string, number>()
    for (let offset = 0; offset < addresses.length; offset++) {
      const at = start + offset
      if (consumed[at] || tokens[at] !== addresses[offset]) return false
      needed.set(addresses[offset], (needed.get(addresses[offset]) ?? 0) + 1)
    }
    return [...needed].every(
      ([address, count]) => (survivorCounts.get(address) ?? 0) >= count,
    )
  }
  const survivorChips: Array<{ label: string; at: number }> = []
  for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex++) {
    if (!isSurvivor(tokenIndex)) continue
    const roleLabel = roleMentions.find(label =>
      repeatedRoleRunFits(tokenIndex, chipAddresses.get(label) ?? []),
    )
    if (roleLabel) {
      const length = chipAddresses.get(roleLabel)?.length ?? 0
      for (let offset = 0; offset < length; offset++)
        claimSurvivor(tokenIndex + offset)
      survivorChips.push({ label: roleLabel, at: tokenIndex })
      tokenIndex += length - 1
      continue
    }
    survivorChips.push({
      label: staffLabelByAddress.get(tokens[tokenIndex]) ?? tokens[tokenIndex],
      at: tokenIndex,
    })
    claimSurvivor(tokenIndex)
  }

  const chips = [
    ...roleMentions.map(label => ({
      label,
      at: anchorIndexByChip.get(label) ?? UNANCHORED,
    })),
    ...staffMentions.map(label => ({
      label,
      at: anchorIndexByChip.get(label) ?? UNANCHORED,
    })),
    ...survivorChips,
  ]
  return chips
    .map((chip, originalIndex) => ({ chip, originalIndex }))
    .sort(
      (first, second) =>
        first.chip.at - second.chip.at ||
        first.originalIndex - second.originalIndex,
    )
    .map(({ chip }) => chip.label)
}

// Always CONDITIONAL; ALWAYS is reserved for legacy-modal push containers.
export function editorToWorkflowGroups(
  groups: EditorActionGroup[],
  roles: RoleWithMembers[],
  staff: Staff[],
  _availableIntegrations: Integration[],
): {
  pairs: Array<{ api: ApiActionGroup; editor: EditorActionGroup }>
} {
  const pairs: Array<{ api: ApiActionGroup; editor: EditorActionGroup }> = []

  for (const g of groups) {
    const apiActions: ApiAction[] = []

    for (const row of g.actions) {
      if (row.type === 'TRANSFER' && g.transfer) {
        const transfer = editorRowToTransferAction(
          row,
          g.transfer,
          roles,
          staff,
        )
        // No recipients means no rows, as for a phone row that reaches nobody.
        const { recipients } = transfer.parameters as {
          recipients: RecipientEntry[]
        }
        if (recipients.length > 0) apiActions.push(transfer)
        continue
      }
      apiActions.push(...editorRowToApiActions(row, roles, staff))
    }

    if (apiActions.length === 0) continue

    pairs.push({
      api: {
        id: g.id,
        name: g.name || null,
        selectionCriteria: g.when || null,
        selectionType: 'CONDITIONAL',
        actions: apiActions,
      },
      editor: g,
    })
  }

  return { pairs }
}

// One entry per number. The API builds the PHONE_CALL rows from the entries.
export function editorRowToTransferAction(
  row: EditorActionRow,
  transfer: TransferSettings,
  roles: RoleWithMembers[],
  staff: Staff[],
): ApiAction {
  const recipients: RecipientEntry[] = rowRecipients(row, roles, staff).map(
    ({ value, sourceRoleMemberId, staffId }) => {
      const { phoneNumber, extension } = extractPhoneAndExtension(value)
      return {
        number: phoneNumber,
        ...(extension ? { extension } : {}),
        sourceRoleMemberId,
        ...(staffId ? { staffId } : {}),
      }
    },
  )
  return {
    actionDefinition: { actionType: 'TRANSFER' },
    parameters: { ...transfer, recipients },
  }
}

export function editorRowToApiActions(
  row: EditorActionRow,
  roles: RoleWithMembers[],
  staff: Staff[],
): ApiAction[] {
  if (row.type === 'PUSH_INTEGRATION') {
    const seenPushKeys = new Set<string>()
    const pushActions: ApiAction[] = []
    for (const pushTarget of row.pushTargets ?? []) {
      const variantId =
        (pushTarget.parameters?.taskVariant as string | undefined) ?? ''
      const key = `${pushTarget.integrationId}:${pushTarget.actionSubtype ?? ''}:${variantId}`
      if (seenPushKeys.has(key)) continue
      seenPushKeys.add(key)
      pushActions.push({
        actionDefinition: { actionType: 'PUSH_INTEGRATION' },
        parameters: pushTarget.parameters ?? {},
        integrationId: pushTarget.integrationId,
        actionSubtype: pushTarget.actionSubtype,
      } as ApiAction)
    }
    return pushActions
  }

  return rowRecipients(row, roles, staff).map(
    ({ value, sourceRoleMemberId }) => {
      if (row.type === 'EMAIL') {
        return {
          actionDefinition: { actionType: 'EMAIL' },
          parameters: { email: value, number: null },
          sourceRoleMemberId,
        }
      }
      return {
        actionDefinition: { actionType: row.type },
        parameters: { number: value, email: null },
        sourceRoleMemberId,
      }
    },
  )
}

function rowRecipients(
  row: EditorActionRow,
  roles: RoleWithMembers[],
  staff: Staff[],
): RowRecipient[] {
  const channel: 'phone' | 'email' = row.type === 'EMAIL' ? 'email' : 'phone'
  const targets = parseTargets(row.target, channel, roles, staff)

  // Persist every mention faithfully; dedup is deferred to action execution.
  // Each recipient carries the RoleMember it was expanded from (null for a
  // hand-typed literal / direct staff mention) so the action is stamped with
  // sourceRoleMemberId and reorder can identify it by that link.
  const recipients: RowRecipient[] = []

  for (const target of targets) {
    if (target.kind === 'literal-phone' || target.kind === 'literal-email') {
      recipients.push({ value: target.value, sourceRoleMemberId: null })
    } else if (target.kind === 'staff') {
      const staffMember = staff.find(({ id }) => id === target.staffId)
      const address =
        channel === 'email' ? staffMember?.email : staffMember?.phone
      if (address)
        recipients.push({
          value: address,
          sourceRoleMemberId: null,
          staffId: target.staffId,
        })
    } else if (target.kind === 'role') {
      const role = roles.find(({ id }) => id === target.roleId)
      if (!role) continue
      for (const member of role.members) {
        const address =
          channel === 'email' ? member.staff.email : member.staff.phone
        if (address)
          recipients.push({ value: address, sourceRoleMemberId: member.id })
      }
    }
  }
  return recipients
}

export function deriveInstructionsSummary(groups: EditorActionGroup[]): string {
  // Plain-text summary saved on Workflow.handoffInstructions for backward
  // compat. Not used by the editor — the structured shape drives runtime.
  const parts: string[] = []
  for (const group of groups) {
    const condition = group.when ? `When ${group.when}` : 'Always'
    const steps = group.actions.map(action => {
      if (action.type === 'PUSH_INTEGRATION') {
        const count = action.pushTargets?.length ?? 0
        return `push to ${count} integration${count === 1 ? '' : 's'}`
      }
      const verb =
        action.type === 'EMAIL'
          ? 'email'
          : action.type === 'SMS'
            ? 'text'
            : 'transfer to'
      return `${verb} ${action.target}`
    })
    parts.push(`${condition}: ${steps.join('; ')}.`)
  }
  return parts.join(' ')
}
